/**
 * widgets/notification-center/ui/NotificationBell — Bell trigger with unread badge.
 *
 * Renders a bell icon button. If there are unread notifications, overlays a
 * count badge on top of the bell. Opens/closes the dropdown on click.
 *
 * FSD: widgets may import from entities and shared only.
 */

import { Bell } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { useUnreadCountQuery } from '@/entities/notification';

interface NotificationBellProps {
  /** Whether the dropdown is currently open. */
  isOpen?: boolean;
  /** Called when the bell is clicked. */
  onClick: () => void;
}

export function NotificationBell({ isOpen = false, onClick }: NotificationBellProps) {
  const { t } = useTranslation('notifications');
  const { data } = useUnreadCountQuery();
  const unreadCount = data?.unread_count ?? 0;

  return (
    <Button
      variant="ghost"
      size="sm"
      aria-label={t('bell')}
      aria-expanded={isOpen}
      aria-haspopup="true"
      data-testid="notification-bell"
      onClick={onClick}
      className="relative"
    >
      <Bell className="h-4 w-4" aria-hidden="true" />
      {unreadCount > 0 && (
        <Badge
          variant="destructive"
          aria-label={t('unreadCount', { count: unreadCount })}
          data-testid="unread-badge"
          className="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-xs leading-none"
        >
          {unreadCount > 99 ? '99+' : unreadCount}
        </Badge>
      )}
    </Button>
  );
}
